<template>
  <div class="widget-section">
    <div class="widget-grid">

      <div class="card">
        <div class="card-header">
          <i class="fa fa-gear card-header--icon"></i>
          <span class="card-header--title">Widget Settings</span>
        </div>
        <div class="card-content">

          <form class="card-content--form">

            <div class="form-group">
              <label>Affiliation Code</label>
              <select id="selectAffiliateCode" v-model="selectedMastercode" @change="updateAffiliationCode">
                <option v-for="(code, codeIndex) in masterCodeArray" :value="code" selected=""
                  :key="'code-' + codeIndex">
                  {{ code }}
                </option>
              </select>
            </div>

            <div class="form-group" v-if="availableWhitelabelDomains.length > 0">
              <label>Widget Domain</label>
              <select id="selectWidgetDomain" v-model="selectedWhitelabelDomain" @change="updateWhitelabelDomain">
                <option value="">Ferryhopper (default)</option>
                <option v-for="(domain, domainIndex) in availableWhitelabelDomains" :value="domain"
                  :key="'domain-' + domainIndex">
                  {{ domain }}
                </option>
              </select>
              <small class="field-hint">Select a whitelabel domain to use for the widget iframe</small>
            </div>


            <div class="form-group">
              <label>Language</label>
              <select id="selectLanguage" v-model="selectedLanguage" @change="updateWidgetLocale">
                <option value="el">Greek</option>
                <option value="en" selected="true">English</option>
                <option value="it">Italian</option>
                <option value="fr">French</option>
                <option value="de">German</option>
                <option value="es">Spanish</option>
                <option value="pt">Portuguese</option>
                <option value="tr">Turkish</option>
                <option value="bg">Bulgarian</option>
                <option value="pl">Polish</option>
                <option value="hr">Croatian</option>
                <option value="sv">Swedish</option>
                <option value="da">Danish</option>
                <option value="nl">Nederlands</option>
                <option value="sq">Albanian</option>
              </select>
            </div>

            <div class="form-group">
              <label>Size</label>
              <div class="btn-group">
                <button v-for="flavor in widgetFlavors" :key="'widget-flavor-' + flavor.type" type="button"
                  :class="{ 'active': flavor.type === selectedVariation }" @click="setWidgetFlavor(flavor.type)">
                  {{ flavor.title }}
                </button>
              </div>
            </div>

            <!-- Pre-filled Search Values Section -->
            <div class="form-section">
              <h4 class="form-section-title">Pre-filled Search Values</h4>

              <div class="form-group">
                <label>Origin Port</label>
                <select v-model="widget.fromPort.value">
                  <option value="">-- Select origin port --</option>
                  <option v-for="port in availablePorts" :value="port.portCode" :key="'origin-' + port.portCode">
                    {{ port.portName }} ({{ port.portCode }})
                  </option>
                </select>
              </div>

              <div class="form-group">
                <label>Destination Port</label>
                <select v-model="widget.toPort.value">
                  <option value="">-- Select destination port --</option>
                  <option v-for="port in availablePorts" :value="port.portCode" :key="'dest-' + port.portCode">
                    {{ port.portName }} ({{ port.portCode }})
                  </option>
                </select>
              </div>

              <div class="form-group">
                <label class="checkbox">
                  <input class="checkbox-input" type="checkbox" v-model="widget.oneway.value" />
                  <span class="checkbox-label">One-way trip (default is round-trip)</span>
                </label>
              </div>
            </div>

            <!-- Display Options Section -->
            <div class="form-section">
              <h4 class="form-section-title">Display Options</h4>

              <div class="form-group">
                <label class="checkbox">
                  <input class="checkbox-input" type="checkbox" v-model="showColorInput" @change="customColorChecked" />
                  <span class="checkbox-label">Customize Primary Color</span>
                </label>
                <template v-if="showColorInput">
                  <input type="color" v-model="widget.themeColor.value" style="margin-left: 0.5rem;" />
                </template>
              </div>

              <div class="form-group">
                <label class="checkbox">
                  <input class="checkbox-input" type="checkbox" v-model="widget.noBorder.value" />
                  <span class="checkbox-label">Hide border</span>
                </label>
              </div>

              <div class="form-group">
                <label class="checkbox">
                  <input class="checkbox-input" type="checkbox" v-model="widget.noTitle.value" />
                  <span class="checkbox-label">Hide title</span>
                </label>
              </div>

              <div class="form-group">
                <label class="checkbox">
                  <input class="checkbox-input" type="checkbox" v-model="widget.noLogo.value" />
                  <span class="checkbox-label">Hide logo</span>
                </label>
              </div>
            </div>

          </form>
        </div>
      </div>

      <div>

        <div class="card">
          <div class="card-header card-header--right">
            <span class="card-header--title">Affiliation URL</span>
          </div>

          <div class="card-content">
            <p class="code-description">Use this URL to redirect users to Ferryhopper with your affiliate code:</p>
            <div class="code-block">
              <pre><code>{{ affiliationUrl }}</code></pre>
              <button class="copy-btn copy-btn--code" @click="copyToClipboard(affiliationUrl)">
                <i class="fa fa-clipboard"></i>
              </button>
            </div>
          </div>
        </div>

        <div class="card">
          <div class="card-header card-header--right">
            <span class="card-header--title">Search Widget Preview</span>
          </div>
          <div class="card-content">
            <WidgetPreview :formModel="widget"></WidgetPreview>
          </div>
        </div>
      </div>

    </div>
  </div>

</template>

<script>
import WidgetFormModel from '@/models/WidgetFormModel'
import WidgetPreview from '@/components/UIComponents/WidgetPreview'
import PortsRepository from '@/repositories/ports'
import { mapGetters } from 'vuex';

export default {
  components: {
    WidgetPreview
  },
  data() {
    return {
      widget: new WidgetFormModel(),
      attributes: [],
      inputCreationFilters: ['variant', 'language', 'affiliationCode'],
      selectedVariation: 'simple',
      selectedLanguage: 'en',
      selectedMastercode: '',
      selectedWhitelabelDomain: '',
      showColorInput: false,
      widgetFlavors: [
        {
          type: 'simple',
          title: 'Normal',
        },
        {
          type: 'small',
          title: 'Small',
        },
        {
          type: 'xsmall',
          title: 'Extra small',
        },
      ],
    }
  },
  created() {
    this.widget.attributesForComponentInputs()
    this.selectedVariation = this.widget.variant.value
    this.selectedLanguage = this.widget.language.value
    if (this.getAppUser && this.getAppUser.affiliationCodes.length > 0) {
      this.selectedMastercode = this.getAppUser.affiliationCodes[0];
      this.updateAffiliationCode()
    }
  },
  watch: {
    getAppUser(oldValue, newValue) {
      if (newValue.affiliationCodes.length > 0) {
        this.selectedMastercode = this.getAppUser.affiliationCodes[0];
        this.updateAffiliationCode()
      }
    }
  },
  computed: {
    ...mapGetters({
      getAppUser: 'authentication/getAppUser'
    }),
    affiliationUrl() {
      const baseDomain = this.selectedWhitelabelDomain
        ? `https://${this.selectedWhitelabelDomain}`
        : 'https://www.ferryhopper.com';

      const url = new URL(`${baseDomain}/${this.selectedLanguage}/`);
      url.searchParams.set('aff_uid', this.selectedMastercode);
      url.searchParams.set('utm_source', 'affiliate-link');
      url.searchParams.set('utm_medium', 'in-house');
      url.searchParams.set('utm_campaign', this.selectedMastercode);
      url.searchParams.set('utm_content', this.getAppUser?.name);

      return url.toString();
    },
    //used to create the options of affiliation Code select
    masterCodeArray: function () {
      return this.getAppUser?.affiliationCodes || []
    },
    // Available whitelabel domains from user data
    availableWhitelabelDomains: function () {
      return this.getAppUser?.activeWhitelabelDomains || []
    },
    // Available ports from PortsRepository
    availablePorts() {
      return PortsRepository.getPorts();
    },
  },
  methods: {
    onColorChange(value) {
      this.widget.themeColor.value = value
    },
    inputComponentListening(value, type) {
      this.widget[type].value = value
    },
    //listen for the WidgetFlavorSelector component emit
    setWidgetFlavor: function (value) {
      this.selectedVariation = value
      this.widget.variant.value = value
    },
    updateWidgetLocale: function () {
      this.widget.language.value = this.selectedLanguage
    },
    updateAffiliationCode: function () {
      this.widget.affiliationCode.value = this.selectedMastercode
    },
    // Update the whitelabel domain based on dropdown selection
    updateWhitelabelDomain() {
      this.widget.whitelabelDomain.value = this.selectedWhitelabelDomain || '';
    },
    // if the checkbox isn t checked ferryhopper color is set as default theme color for the widget
    // else affiliates color is set as default and an input shows up so that the user can change theme color of the widget
    customColorChecked: function () {
      if (this.showColorInput) {
        this.widget.themeColor.value = this.getAppUser.color || '#01C3A7';
      } else {
        this.widget.resetColorOnly();
      }
    },
    copyToClipboard(text) {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('Copy');
        document.body.removeChild(textarea);
      }
    },
  },
}
</script>


<style scoped>
.widget-section {
  padding: 2rem 0 2rem 0;
}

.widget-grid {
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 2rem;
}

.form-section {
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid #e0e0e0;
}

.form-section-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 1rem;
  margin-top: 0;
}

.text-input {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 0.9rem;
  margin-top: 0.25rem;
}

.text-input:focus {
  outline: none;
  border-color: #01C3A7;
  box-shadow: 0 0 0 2px rgba(1, 195, 167, 0.2);
}

.field-hint {
  display: block;
  font-size: 0.75rem;
  color: #888;
  margin-top: 0.25rem;
}

.whitelabel-notice {
  display: block;
  font-size: 0.8rem;
  color: #01C3A7;
  margin-top: 0.5rem;
}

.whitelabel-notice i {
  margin-right: 0.25rem;
}

.code-description {
  color: #666;
  margin-bottom: 0.75rem;
  font-size: 0.9rem;
}

.code-block {
  position: relative;
  background-color: #2d2d2d;
  border-radius: 6px;
  overflow: hidden;
}

.code-block pre {
  margin: 0;
  padding: 1rem;
  overflow-x: auto;
}

.code-block code {
  background: none;
  color: #f8f8f2;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.85rem;
  line-height: 1.5;
  white-space: pre;
}

.copy-btn--code {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background-color: rgba(255, 255, 255, 0.1);
  border: none;
  color: #fff;
  padding: 0.4rem 0.6rem;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.copy-btn--code:hover {
  background-color: rgba(255, 255, 255, 0.2);
}

@media (max-width: 900px) {
  .widget-grid {
    grid-template-columns: 1fr;
  }
}
</style>
